import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ContentPlanRootState as S } from '../../elements/contentPlan/store';
import { ElementAction } from '../../elements/elementAction';
import { Metadata, Schema, SchemaField } from '../../types/Metadata';
import { Functions } from '../../types/types';
import { View } from '../../types/View';
import { setLoading } from '../Loader/loaderAction';
import { updateViewState, updateSelectedView } from '../View/viewReducer';
import { ViewType } from '../../enums/ViewType';
import { clone, excludeEmptyFilters } from '../../utils';
import { FieldType } from '../../enums/FieldType';
import { NodeType } from '../../enums/NodeType';
import { DataType } from '../../enums/DataType';
import { loadPermissions } from '../ContentPlanning/contentPlanningActions';
import { updateAdministrationState } from './administrationReducer';
import { Roles } from './RoleField/roleField';
import { AdminDomain } from '../../enums/AdminDomain';
import { ViewCategory } from '../../enums/viewCategory';
import { FieldData } from '../../types/Field';

const NUM_PER_PAGE = 25;

const getSchemaFields = (schema: Schema) =>
  schema.props.fields.map(f => f.key).map(field => ({ field }));

export const viewAdminData =
  (schema?: Schema): ElementAction<Functions, S> =>
  async dispatch => {
    const fields = getSchemaFields(schema);
    const view = {
      id: schema.id,
      props: {
        refId: schema.id,
        name: schema.props.name,
        description: schema.props.description,
        type: ViewType.AdministrationTable,
        filters: fields
      },
      category: ViewCategory.Administration,
      schema,
      dataSchema: schema,
      card: {
        props: {
          data: schema.props.type,
          fields
        }
      }
    } as View.Setting;
    dispatch(updateSelectedView(view));
    dispatch(
      updateViewState(view, {
        filters: schema.props.fields.map(f => ({ field: f.key }))
      })
    );
    dispatch(loadData());
  };

const setFilter =
  (filter: View.FiltersField): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view } = getState();
    let updated = false;
    const filters =
      view?.filters
        ?.map(f => {
          if (f.field == filter.field) {
            updated = true;
            return { ...f, ...filter };
          }
          return f;
        })
        .filter(x => x) || [];
    if (!updated) {
      filters.push(filter);
    }

    dispatch(updateViewState(view?.selected, { filters }));
    //if (reload) {
    dispatch(reloadData());
    //}
  };

let timeout = null;
export const reloadData =
  (forceReload?: boolean): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(async () => {
      const { view } = getState();
      if (!view?.selected) return;
      dispatch(loadData({ forceReload, ignoreLoading: true }));
    }, 400);
  };

const increaseNumOfResultsAndLoad =
  (cb?: Function): ElementAction<Functions, S> =>
  async (dispatch, getState, o) => {
    const { pagination } = getState().view;
    await loadData({
      forceReload: true,
      ignoreLoading: true,
      numOfResults: pagination.npp + NUM_PER_PAGE
    })(dispatch, getState, o);
    if (cb) cb();
  };

interface LoadDataOpts {
  forceReload?: boolean;
  ignoreLoading?: boolean;
  numOfResults?: number;
}
const loadData =
  ({
    ignoreLoading,
    numOfResults = NUM_PER_PAGE
  }: LoadDataOpts = {}): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view, planning } = getState();
    if (!view?.selected) return;
    const selView = view.selected;
    if (!ignoreLoading) dispatch(setLoading(true));
    try {
      const pagination = {
        npp: numOfResults,
        hasMore: true
      };
      if (selView.id == AdminDomain.permissions) {
        const { searchPermission } = getAPI();
        const data = await searchPermission({
          workspaceRefId: planning.selectedWorkspace.props.refId,
          filters: excludeEmptyFilters(view.filters),
          maxResults: numOfResults + 1
        });
        if (data?.length <= numOfResults) pagination.hasMore = false;
        else if (data) data.pop();
        dispatch(
          updateViewState(selView, {
            data: mapPermissionData(data),
            pagination
          })
        );
      } else if (selView.id == AdminDomain.stagedTags) {
        const { searchStagedTags } = getAPI();
        const data = await searchStagedTags({
          filters: excludeEmptyFilters(view.filters),
          maxResults: numOfResults + 1
        });
        if (data?.length <= numOfResults) pagination.hasMore = false;
        else if (data) data.pop();
        dispatch(
          updateViewState(selView, {
            data,
            pagination
          })
        );
      }
    } finally {
      if (!ignoreLoading) dispatch(setLoading(false));
    }
  };

const showForm =
  (data: Partial<Metadata>, schema?: Schema): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    const s = schema || getState().view.selected.dataSchema;
    dispatch(updateAdministrationState({ data: clone(data), schema: s }));
  };

const closeForm = (): ElementAction<Functions, S> => (dispatch, getState) => {
  dispatch(updateAdministrationState({ schema: null, data: null }));
};

const save =
  (addAdditional?: boolean): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { administration, planning } = getState();
    const { data, schema } = administration;
    if (schema.id == AdminDomain.permissions) {
      const { upsertPermission } = getAPI();
      const { roles, employee } = data.props || {};
      if (!employee?.length) {
        throw new Error('Employee is required.');
      }
      const empCd = employee[0].props.empCd;
      await upsertPermission({
        workspaceRefId: planning.selectedWorkspace.props.refId,
        empCd,
        roles
      });
      addAdditional ? dispatch(showForm({})) : dispatch(closeForm());
      dispatch(loadData());
      dispatch(reloadPermissions(empCd));
    }
  };

const reloadPermissions =
  (empCd: number): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    if (empCd == getState().app?.user?.employeeId) {
      dispatch(loadPermissions());
    }
  };

const deleteData =
  (d: Metadata): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view, planning } = getState();
    if (d && view?.selected?.id == AdminDomain.permissions) {
      const { deletePermission } = getAPI();
      const { employee } = d.props;
      dispatch(setLoading(true));
      const empCd = employee[0].props.empCd;
      await deletePermission({
        workspaceRefId: planning.selectedWorkspace.props.refId,
        empCd
      });
      await dispatch(loadData());
      dispatch(setLoading(false));
      dispatch(reloadPermissions(empCd));
    }
  };

const updateField =
  (field: SchemaField, value: any): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    dispatch(updateFields([{ field, value }]));
  };

const updateFields =
  (fields: FieldData[]): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    const { data } = getState().administration;
    if (!data) return;
    if (!data.props) data.props = {};
    fields.forEach(({ field, value }) => {
      data.props[field.field] = value;
    });
    dispatch(updateAdministrationState({ data: { ...data } }));
  };

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  return {
    showForm: (data: Partial<Metadata>, schema?: Schema) =>
      dispatch(showForm(data, schema)),
    updateField: (f: SchemaField, v: any) => dispatch(updateField(f, v)),
    updateFields: (fields: FieldData[]) => dispatch(updateFields(fields)),
    deleteData: (d: Metadata) => dispatch(deleteData(d)),
    closeForm: () => dispatch(closeForm()),
    save: (addAdditional?: boolean) => dispatch(save(addAdditional)),
    viewAdminData: (id: AdminDomain) => {
      const s = getAdministrationDomains().filter(s => s.id == id)[0];
      dispatch(viewAdminData(s));
    },
    setFilter: (filter: View.FiltersField) => dispatch(setFilter(filter)),
    loadData: (ignoreLoading?: boolean, numOfResults?: number) =>
      dispatch(loadData({ ignoreLoading, numOfResults })),
    increaseNumOfResultsAndLoad: (cb?: Function) =>
      dispatch(increaseNumOfResultsAndLoad(cb))
  };
};

const mapPermissionData = (employees: Metadata[]): Metadata[] =>
  employees?.map(e => ({
    id: e.id,
    labels: ['Permission'] as any,
    props: {
      name: e.props.name,
      refId: e.props.refId,
      roles: e.props.roles,
      employee: [e]
    }
  }));

export const getAdministrationDomains = () => {
  return [
    {
      id: AdminDomain.permissions,
      labels: ['Schema', 'WorkspaceSchema'],
      props: {
        name: 'Permission',
        description: 'Workspace Permission Setting',
        type: 'Permission',
        fields: [
          {
            field: 'employee',
            name: 'Employee',
            fieldType: FieldType.reference,
            dataType: NodeType.Employee,
            key: 'Permission.employee',
            displayName: 'Employee'
          },
          {
            field: 'roles',
            name: 'Role',
            options: Roles,
            fieldType: FieldType.computed,
            dataType: DataType.string,
            key: 'Permission.roles',
            displayName: 'Role'
          }
        ]
      }
    },
    {
      id: AdminDomain.stagedTags,
      labels: ['Schema', 'WorkspaceSchema'],
      props: {
        name: 'Publish Staged Tags',
        description: 'Publish Staged Tags',
        type: 'PublishStagedTags',
        fields: [
          {
            field: 'taxonomyTags',
            name: 'Perennial Priorities (Staged)',
            fieldType: FieldType.reference,
            dataType: NodeType.TaxonomyTag,
            multiValue: true,
            parentField: 'backtag',
            key: 'PublishStagedTags.taxonomyTags',
            displayName: 'Perennial Priorities (Staged)'
          },
          {
            field: 'rolePriorities',
            name: 'Mission-Critical Priorities/Journey Steps (Staged)',
            fieldType: FieldType.reference,
            dataType: NodeType.RolePriority,
            multiValue: true,
            parentField: 'backtag',
            key: 'PublishStagedTags.rolePriorities',
            displayName: 'Mission-Critical Priorities/Journey Steps (Staged)'
          },
          {
            field: 'createdBy',
            name: 'Published By/On',
            fieldType: FieldType.reference,
            dataType: NodeType.Employee,
            relationType: 'CREATED_BY',
            key: 'PublishStagedTags.createdBy',
            displayName: 'Published By/On'
          },

          {
            field: 'numOfItemsTagged',
            name: '# of Items Tagged',
            fieldType: FieldType.static,
            dataType: DataType.number,
            key: 'PublishStagedTags.numOfItemsTagged',
            displayName: '# of Items Tagged'
          },
          {
            field: 'numOfItemsProcessed',
            name: '# of Items Processed',
            fieldType: FieldType.static,
            dataType: DataType.number,
            key: 'PublishStagedTags.numOfItemsProcessed',
            displayName: '# of Items Processed'
          },
          {
            field: 'completedOn',
            name: 'Completed On',
            fieldType: FieldType.static,
            dataType: DataType.dateTime,
            key: 'PublishStagedTags.completedOn',
            displayName: 'Completed On'
          }
        ]
      }
    }
  ] as any as Schema[];
};
